// Modules
import { useState, useEffect } from "react"
import ReactMarkdown from "react-markdown"

// Constants
import PATHS, { getFullPath } from "~constants/paths"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"

// Components
import BackLink from "~components/BackLink"
import MessageModal from "~components/messages/MessageModal"
import NoCoverImage from "~components/NoCoverImage"
import { CoveredImage } from "~styled/image"
import SkeletonWrapper from "~components/SkeletonWrapper"
import MessageRequestModal from "~components/messages/MessageRequestModal"
import { ButtonPrimarySoftLarge, createButtonStyle } from "~styled/button"
import {
  FormViewBody,
  FormViewHeader,
  FormViewWrapper,
  FormViewContainer,
  FormViewCoverImage,
} from "~styled/form"

// Interfaces
import { MessageShape } from "~shapes/message"
import { OnRequestCloseProps } from "~shapes/modal"
import { useLocation } from "react-router-dom"

export interface MessageDetailProps {
  message: MessageShape
  isLoading: boolean
}

const MessageDetail = ({ message, isLoading }: MessageDetailProps) => {
  const [messageData, setMessageData] = useState<MessageShape>(message)
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false)
  const location = useLocation()
  const state = location.state as { navigateFrom?: string } | null
  const navigateFrom = state?.navigateFrom
  const navTo = navigateFrom || getFullPath(PATHS.messages)

  useEffect(() => {
    setMessageData(message)
  }, [message])

  const handleEditMessage = () => {
    setIsModalOpened(true)
  }

  const handleOnRequestClose = ({ data }: OnRequestCloseProps) => {
    const message: MessageShape = data
    if (message) setMessageData({ ...message, company: messageData.company })

    setIsModalOpened(false)
  }

  return (
    <>
      {/* Back */}
      <BackLink
        text={
          !!navigateFrom ? `Back to Company Messages` : `Back to Communication`
        }
        path={navTo}
      />

      {/* Wrapper */}
      <FormViewWrapper>
        {/* Cover Image */}
        <FormViewCoverImage>
          <SkeletonWrapper isLoading={isLoading} height={200}>
            {!message?.cover ? (
              <NoCoverImage />
            ) : (
              <CoveredImage
                src={message?.cover}
                height={200}
                alt={message?.cover}
              />
            )}
          </SkeletonWrapper>
        </FormViewCoverImage>

        <FormViewContainer>
          <div style={createButtonStyle}>
            <ButtonPrimarySoftLarge
              onClick={handleEditMessage}
              disabled={isLoading}
            >
              Edit Message
            </ButtonPrimarySoftLarge>
          </div>

          {/* Header */}
          <FormViewHeader>
            <SkeletonWrapper isLoading={isLoading}>
              <h2>
                {messageData?.name} - {messageData?.code}
              </h2>
            </SkeletonWrapper>
          </FormViewHeader>

          {/* Body */}
          <FormViewBody>
            <h3>Message Body</h3>
            <SkeletonWrapper isLoading={isLoading} count={1}>
              <ReactMarkdown>
                {messageData?.description.replace(/<br\s*\/?>/gi, "&nbsp; \n")}
              </ReactMarkdown>
            </SkeletonWrapper>
          </FormViewBody>
        </FormViewContainer>
      </FormViewWrapper>

      {isModalOpened && (
        <>
          <IsAuthorized action={RBAC_RULES.CREATE_MESSAGE}>
            <MessageModal
              message={messageData}
              onRequestClose={handleOnRequestClose}
            />
          </IsAuthorized>

          <IsAuthorized action={RBAC_RULES.CREATE_MESSAGE_REQUEST}>
            <MessageRequestModal
              message={messageData}
              onRequestClose={handleOnRequestClose}
            />
          </IsAuthorized>
        </>
      )}
    </>
  )
}

export default MessageDetail
